import { MouseEvent, JSX, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Slider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
} from '@mui/material'
import { GridBreakpoint, GridLayout, GridContainer, GridColumn, GridContainerTitle, GridContainerName } from '../../../interfaces/IGridSettings'
import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined'
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined'
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined'
import MapOutlinedIcon from '@mui/icons-material/MapOutlined'
import MarkUnreadChatAltOutlinedIcon from '@mui/icons-material/MarkUnreadChatAltOutlined'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import AttachmentOutlinedIcon from '@mui/icons-material/AttachmentOutlined'
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft'
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter'
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight'
import { EventDto } from '../../../interfaces/IEvent'
import { EventType } from '../../../interfaces/IEventEnums'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'

interface GridLayoutProps {
  viewEvent: EventDto
  gridLayout: GridLayout
  onChange: (gridLayout: GridLayout) => void
}

export default function GridLayoutSelection({ viewEvent, gridLayout, onChange }: GridLayoutProps) {
  const [grid, setGrid] = useState<JSX.Element[]>([])
  const [selectedContainer, setSelectedContainer] = useState<GridContainer>(
    gridLayout.columns?.find((column) => column.container.length > 0)?.container[0] as GridContainer,
  )
  const [selectedColumn, setSelectedColumn] = useState<GridColumn>(gridLayout.columns?.find((column) => column.container.length > 0) as GridColumn)

  const containerIcons: { [key: string]: JSX.Element } = {
    [GridContainerName.detailsContainer]: <SettingsOutlinedIcon />,
    [GridContainerName.affectedCustomersContainer]: <PeopleAltOutlinedIcon />,
    [GridContainerName.alertContainer]: <NotificationsNoneOutlinedIcon />,
    [GridContainerName.mapContainer]: <MapOutlinedIcon />,
    [GridContainerName.postContainer]: <MarkUnreadChatAltOutlinedIcon />,
    [GridContainerName.checkListContainer]: <FactCheckOutlinedIcon />,
    [GridContainerName.documentContainer]: <AttachmentOutlinedIcon />,
    [GridContainerName.assignmentContainer]: <AssignmentOutlinedIcon />,
  }

  const containerColors: { [key: string]: string } = {
    [GridContainerName.detailsContainer]: '#f44336',
    [GridContainerName.affectedCustomersContainer]: '#e91e63',
    [GridContainerName.alertContainer]: '#9c27b0',
    [GridContainerName.mapContainer]: '#673ab7',
    [GridContainerName.postContainer]: '#3f51b5',
    [GridContainerName.checkListContainer]: '#2196f3',
    [GridContainerName.documentContainer]: '#03a9f4',
    [GridContainerName.assignmentContainer]: '#00bcd4',
  }

  const handleResetGridLayout = () => {
    if (viewEvent?.type === EventType.Generell) {
      onChange({
        width: GridBreakpoint.xxl,
        align: 'center',
        columns: [
          {
            id: 1,
            container: [
              {
                title: GridContainerTitle.detailsTitle,
                name: GridContainerName.detailsContainer,
                width: 12,
              },
              {
                title: GridContainerTitle.documentTitle,
                name: GridContainerName.documentContainer,
                width: 12,
              },
              {
                title: GridContainerTitle.assignmentTitle,
                name: GridContainerName.assignmentContainer,
                width: 12,
              },
            ],
            width: 3,
            align: 'left',
          },
          {
            id: 2,
            container: [
              {
                title: GridContainerTitle.affectedCustomersTitle,
                name: GridContainerName.affectedCustomersContainer,
                width: 12,
              },
              {
                title: GridContainerTitle.alertTitle,
                name: GridContainerName.alertContainer,
                width: 12,
              },
            ],
            width: 4,
            align: 'left',
          },
          {
            id: 3,
            container: [
              {
                title: GridContainerTitle.postTitle,
                name: GridContainerName.postContainer,
                width: 12,
              },
            ],
            width: 5,
            align: 'left',
          },
          {
            id: 4,
            container: [],
            width: 3,
            align: 'left',
          },
          {
            id: 5,
            container: [],
            width: 3,
            align: 'left',
          },
        ],
      })
    }
    if (viewEvent?.type === EventType.Fellesfeil) {
      onChange({
        width: GridBreakpoint.xxl,
        align: 'center',
        columns: [
          {
            id: 1,
            container: [
              {
                title: GridContainerTitle.detailsTitle,
                name: GridContainerName.detailsContainer,
                width: 12,
              },
              {
                title: GridContainerTitle.documentTitle,
                name: GridContainerName.documentContainer,
                width: 12,
              },
              {
                title: GridContainerTitle.assignmentTitle,
                name: GridContainerName.assignmentContainer,
                width: 12,
              },
            ],
            width: 3,
            align: 'left',
          },
          {
            id: 2,
            container: [
              {
                title: GridContainerTitle.affectedCustomersTitle,
                name: GridContainerName.affectedCustomersContainer,
                width: 12,
              },
              {
                title: GridContainerTitle.alertTitle,
                name: GridContainerName.alertContainer,
                width: 12,
              },
              {
                title: GridContainerTitle.mapTitle,
                name: GridContainerName.mapContainer,
                width: 12,
              },
            ],
            width: 4,
            align: 'left',
          },
          {
            id: 3,
            container: [
              {
                title: GridContainerTitle.postTitle,
                name: GridContainerName.postContainer,
                width: 12,
              },
            ],
            width: 5,
            align: 'left',
          },
          {
            id: 4,
            container: [],
            width: 3,
            align: 'left',
          },
          {
            id: 5,
            container: [],
            width: 3,
            align: 'left',
          },
        ],
      })
    }
    if (viewEvent?.type === EventType.Planlagt) {
      onChange({
        width: GridBreakpoint.xxl,
        align: 'center',
        columns: [
          {
            id: 1,
            container: [
              {
                title: GridContainerTitle.detailsTitle,
                name: GridContainerName.detailsContainer,
                width: 12,
              },
              {
                title: GridContainerTitle.documentTitle,
                name: GridContainerName.documentContainer,
                width: 12,
              },
              {
                title: GridContainerTitle.assignmentTitle,
                name: GridContainerName.assignmentContainer,
                width: 12,
              },
            ],
            width: 3,
            align: 'left',
          },
          {
            id: 2,
            container: [
              {
                title: GridContainerTitle.affectedCustomersTitle,
                name: GridContainerName.affectedCustomersContainer,
                width: 12,
              },
              {
                title: GridContainerTitle.alertTitle,
                name: GridContainerName.alertContainer,
                width: 12,
              },
              {
                title: GridContainerTitle.mapTitle,
                name: GridContainerName.mapContainer,
                width: 12,
              },
            ],
            width: 4,
            align: 'left',
          },
          {
            id: 3,
            container: [
              {
                title: GridContainerTitle.postTitle,
                name: GridContainerName.postContainer,
                width: 12,
              },
              {
                title: GridContainerTitle.checkListTitle,
                name: GridContainerName.checkListContainer,
                width: 12,
              },
            ],
            width: 5,
            align: 'left',
          },
          {
            id: 4,
            container: [],
            width: 3,
            align: 'left',
          },
          {
            id: 5,
            container: [],
            width: 3,
            align: 'left',
          },
        ],
      })
    }

    setSelectedContainer({
      title: GridContainerTitle.detailsTitle,
      name: GridContainerName.detailsContainer,
      width: 12,
    })

    setSelectedColumn({
      id: 1,
      container: [
        {
          title: GridContainerTitle.detailsTitle,
          name: GridContainerName.detailsContainer,
          width: 12,
        },
      ],
      width: 3,
      align: 'left',
    })
  }

  const getContainer = (container: GridContainer, index: number) => {
    return (
      <Box
        onClick={(e: any) => {
          e.target.value = container.name

          handleSelectContainer(e)
        }}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          border: '2px solid',
          borderColor: containerColors[container.name],
          borderRadius: 1,
          height: 50,
          bgcolor: selectedContainer.name === container.name ? alpha(containerColors[selectedContainer.name], 0.2) : 'transparent',
          cursor: 'pointer'
        }}>
        {containerIcons[container.name]}
      </Box>
    );
  }

  const handleGridLayout = (e: any) => {
    const { name, value } = e.target
    const newValue = Number(value)

    onChange({
      ...gridLayout,
      [name]: newValue,
    })
  }

  const handleSelectedContainerWidthChange = (e: any) => {
    const { value } = e.target
    const newValue = Number(value)

    const newColumns = gridLayout.columns?.map((column) => {
      if (column.container.find((container) => container.name === selectedContainer.name)) {
        return {
          ...column,
          container: column.container.map((container) => {
            if (container.name === selectedContainer.name) {
              return {
                ...container,
                width: newValue,
              }
            }
            return container
          }),
        }
      }
      return column
    })
    setSelectedContainer({
      ...selectedContainer,
      width: newValue,
    })

    onChange({
      ...gridLayout,
      columns: newColumns,
    })
  }

  const handleColumnWidthChange = (e: any) => {
    const { name, value } = e.target
    const newValue = Number(value)

    const newColumns = gridLayout.columns?.map((column) => {
      if (column.id === Number(name)) {
        return {
          ...column,
          width: newValue,
        }
      }
      return column
    })

    onChange({
      ...gridLayout,
      columns: newColumns,
    })

    setSelectedColumn({
      ...selectedColumn,
      width: newValue,
    })
  }

  const handleGridAlignment = (event: MouseEvent<HTMLElement>, newAlignment: string | null) => {
    if (newAlignment !== null) {
      onChange({
        ...gridLayout,
        align: newAlignment,
      })
    }
  }

  const handleColumGridAlignment = (columnId: number, event: MouseEvent<HTMLElement>, newAlignment: string | null) => {
    if (newAlignment !== null) {
      const newColumns = gridLayout.columns?.map((column) => {
        if (column.id === columnId) {
          return {
            ...column,
            align: newAlignment,
          }
        }
        return column
      })

      onChange({
        ...gridLayout,
        columns: newColumns,
      })
    }
  }

  const handleSelectContainer = (e: any) => {
    const { value } = e.target

    const newContainer = gridLayout.columns
      ?.map((column) => {
        return column.container.find((container) => container.name === value)
      })
      .filter((container) => container !== undefined)[0]

    if (!newContainer) return

    setSelectedContainer(newContainer)
  }

  const handleMoveSelectedContainerToSelectedColumn = (e: any) => {
    const { value } = e.target
    const newValue = Number(value)

    const newColumns = gridLayout.columns.map((column) => {
      if (column.container.find((container) => container.name === selectedContainer.name)) {
        return {
          ...column,
          container: column.container.filter((container) => container.name !== selectedContainer.name),
        }
      }
      return column
    })

    const newColumn = newColumns?.find((column) => column.id === newValue)

    if (!newColumn) return

    newColumn.container.push(selectedContainer)

    onChange({
      ...gridLayout,
      columns: newColumns,
    })
  }

  const handleContainerPlacementInColumnArrayChange = (direction: 'up' | 'down') => {
    const newColumns = gridLayout.columns?.map((column) => {
      if (column.container.find((container) => container.name === selectedContainer.name)) {
        const containerIndex = column.container.findIndex((container) => container.name === selectedContainer.name)
        const containerToMove = column.container[containerIndex]
        const newContainerArray = column.container.filter((container) => container.name !== selectedContainer.name)

        if (containerIndex === 0 && direction === 'up') return column

        if (direction === 'up') {
          newContainerArray.splice(containerIndex - 1, 0, containerToMove)
        }
        if (direction === 'down') {
          newContainerArray.splice(containerIndex + 1, 0, containerToMove)
        }

        return {
          ...column,
          container: newContainerArray,
        }
      }
      return column
    })

    onChange({
      ...gridLayout,
      columns: newColumns,
    })
  }

  useEffect(() => {
    if (!gridLayout.columns) return

    let buildGrid: JSX.Element[] = []

    gridLayout.columns.forEach((column, index) => {
      column.container.length > 0 &&
        buildGrid.push(
          <Grid
            key={index}
            size={{
              sm: column.width,
              xs: 12,
            }}
            sx={{
              p: 0.3
            }}
          >
            <Typography
              variant={'body2'}
              color={selectedColumn.id === column.id ? 'primary.main' : 'text.primary'}
              onClick={() => setSelectedColumn(column)}
              sx={{
                textAlign: 'center',
                cursor: 'pointer',
                textDecoration: 'underline'
              }}>
              {`K${column.id}`}
            </Typography>
            <Box
              onClick={() => setSelectedColumn(column)}
              sx={{
                border: '2px double',
                borderColor: selectedColumn.id === column.id ? 'primary.main' : 'default',
                borderRadius: 1,
                p: 0.3,
                zIndex: selectedColumn.id === column.id ? 1 : 0,
                cursor: 'pointer',
                textDecoration: 'underline'
              }}>
              <Grid container spacing={0.5} sx={{
                justifyContent: column.align
              }}>
                {column.container.map((container, index) => {
                  return (
                    <Grid key={index} size={container.width}>
                      {getContainer(container, index)}
                    </Grid>
                  )
                })}
              </Grid>
            </Box>
          </Grid>,
        )
    })

    setGrid(buildGrid)
  }, [gridLayout, selectedContainer, selectedColumn])

  return (
    <>
      <Stack direction={'column'} spacing={2}>
        <Typography
          variant={'body1'}
          sx={{
            fontWeight: 'bold',
            textDecoration: 'underline',
          }}
        >
          Endre visning for {viewEvent?.type.toLowerCase()}
        </Typography>
        <Box sx={{
          width: '100%'
        }}>
          <Typography variant={'body2'}>Visningsbredde</Typography>
          <Slider
            sx={{ width: '100%' }}
            value={gridLayout.width}
            name="width"
            aria-label="width"
            step={null}
            marks={[
              { value: GridBreakpoint.md, label: '900' },
              { value: GridBreakpoint.lg, label: '1200' },
              { value: GridBreakpoint.xl, label: '1536' },
              { value: GridBreakpoint.xxl, label: '1920' },
              { value: GridBreakpoint.xxxl, label: '2400' },
              { value: GridBreakpoint.xxxxl, label: '2880' },
            ]}
            min={GridBreakpoint.md}
            max={GridBreakpoint.xxxxl}
            onChange={handleGridLayout}
          />
        </Box>
        <Stack
          sx={{
            border: '1px dashed',
            borderRadius: 1,
            bgcolor: 'background.default',
          }}
        >
          <Box
            sx={{
              p: 1,
              textAlign: 'center',
              width: '100%',
              alignSelf: 'center'
            }}>
            <ToggleButtonGroup size="small" value={gridLayout.align} exclusive onChange={handleGridAlignment} aria-label="text alignment">
              <ToggleButton value="left" aria-label="left aligned">
                <FormatAlignLeftIcon />
              </ToggleButton>
              <ToggleButton value="center" aria-label="centered">
                <FormatAlignCenterIcon />
              </ToggleButton>
              <ToggleButton value="right" aria-label="right aligned">
                <FormatAlignRightIcon />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Divider
            variant={'fullWidth'}
            sx={{
              borderStyle: 'outset',
            }}
          />
          <Grid container sx={{
            justifyContent: gridLayout.align
          }}>
            {grid}
          </Grid>
        </Stack>
        <Box
          sx={{
            border: '1px dashed',
            p: 2,
            borderRadius: 1
          }}>
          <Box
            sx={{
              p: 1,
              border: '2px solid',
              borderRadius: 1,
              borderColor: containerColors[selectedContainer.name],
              bgcolor: alpha(containerColors[selectedContainer.name], 0.2)
            }}>
            <Stack
              direction={'row'}
              spacing={1}
              sx={{
                justifyContent: 'center',
                alignItems: 'center'
              }}>
              {containerIcons[selectedContainer.name]}
              <Typography variant={'body2'}>{selectedContainer.title}</Typography>
            </Stack>
          </Box>
          <Stack>
            <Typography variant={'body2'}>Bredde</Typography>
            <Slider
              value={selectedContainer.width}
              name={'width'}
              aria-label="width"
              valueLabelDisplay="auto"
              step={1}
              marks
              min={2}
              max={12}
              onChange={handleSelectedContainerWidthChange}
            />
          </Stack>
          <Stack direction={'row'} spacing={2}>
            <FormControl fullWidth>
              <Typography variant={'body2'}>Kolonne</Typography>
              <Select
                name="Velg kolonne"
                size="small"
                displayEmpty
                value={gridLayout.columns?.find((column) => column.container.find((container) => container.name === selectedContainer.name))?.id}
                onChange={handleMoveSelectedContainerToSelectedColumn}
              >
                {gridLayout.columns?.map((column) => {
                  return (
                    <MenuItem key={column.id} value={column.id}>
                      <Typography variant="body2">{`K${column.id}`}</Typography>
                    </MenuItem>
                  )
                })}
              </Select>
            </FormControl>
            <Stack>
              <Typography variant={'body2'}>Plassering</Typography>
              <Stack direction="row" spacing={1}>
                <IconButton
                  color="primary"
                  size="small"
                  sx={{
                    border: '1px solid',
                  }}
                  onClick={() => handleContainerPlacementInColumnArrayChange('up')}
                >
                  <ChevronLeftOutlinedIcon />
                </IconButton>
                <IconButton
                  color="primary"
                  size="small"
                  sx={{
                    border: '1px solid',
                  }}
                  onClick={() => handleContainerPlacementInColumnArrayChange('down')}
                >
                  <ChevronRightOutlinedIcon />
                </IconButton>
              </Stack>
            </Stack>
          </Stack>
        </Box>
        {selectedColumn && (
          <Box
            sx={{
              p: 2,
              width: '100%',
              border: '1px dashed',
              borderRadius: 1
            }}>
            <Stack direction={'column'}>
              <Typography
                variant={'body2'}
                sx={{
                  alignSelf: 'center',
                  textDecoration: 'underline'
                }}>
                {`K${selectedColumn.id} bredde og justering`}
              </Typography>
              <Slider
                key={selectedColumn.id}
                value={selectedColumn.width}
                name={selectedColumn.id.toString()}
                aria-label="width"
                valueLabelDisplay="auto"
                step={1}
                marks
                min={2}
                max={12}
                onChange={handleColumnWidthChange}
              />
              <Box
                sx={{
                  textAlign: 'center',
                  width: '100%',
                  alignSelf: 'center'
                }}>
                <ToggleButtonGroup
                  size="small"
                  value={gridLayout.columns?.find((x) => x.id === selectedColumn.id)?.align}
                  exclusive
                  onChange={(e: MouseEvent<HTMLElement>, value: any) => {
                    handleColumGridAlignment(selectedColumn.id, e, value)
                  }}
                  aria-label="text alignment"
                >
                  <ToggleButton value="left" aria-label="left aligned">
                    <FormatAlignLeftIcon />
                  </ToggleButton>
                  <ToggleButton value="center" aria-label="centered">
                    <FormatAlignCenterIcon />
                  </ToggleButton>
                  <ToggleButton value="right" aria-label="right aligned">
                    <FormatAlignRightIcon />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Stack>
          </Box>
        )}
        <Button variant="outlined" color="primary" onClick={handleResetGridLayout}>
          Tilbakestill
        </Button>
      </Stack>
    </>
  );
}
